import { useCallback, useEffect, useRef, useState } from 'react';
import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import MereniPole from '../../pages/MojeSmena/MereniPole';
import ZpetTlacitko from '../ui/ZpetTlacitko';
import { SECTION_COLOR, STAV_BARVY, STAV_LABELS, Badge, csrf } from './ui';

/**
 * Checklisty → detail checklistu (sloupec vedle seznamu, na mobilu celá
 * obrazovka se šipkou zpět). Dřív modal přes celý modul — z jednoho
 * checklistu do druhého se muselo zavírat a otevírat. Rozpracovaný checklist
 * se vyplňuje přímo tady; číslo měření stejným polem jako v Cockpitu.
 */
export default function VyplneniChecklistu({ checklistId, isDark, isMobile, mutedColor, iStyle, onZmena, onZpet, rezervaDole = null }) {
    const [checklist, setChecklist] = useState(null);
    const nacti = useCallback(async () => {
        const res = await fetch(`/api/checklisty/${checklistId}`, { headers: { Accept: 'application/json' } });
        if (res.ok) setChecklist(await res.json());
    }, [checklistId]);
    useEffect(() => { setChecklist(null); nacti(); }, [nacti]);

    const ulozOdpoved = async (id, polozkaId, data) => {
        await fetch(`/api/checklisty/${id}/odpovedi`, {
            method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
            body: JSON.stringify({ polozka_id: polozkaId, ...data }),
        });
        await nacti();
        onZmena?.();
    };
    const dokoncit = async (id) => {
        const res = await fetch(`/api/checklisty/${id}/dokoncit`, { method: 'POST', headers: { Accept: 'application/json', 'X-CSRF-TOKEN': csrf() } });
        if (res.ok) { await nacti(); onZmena?.(); }
        else { const d = await res.json().catch(() => ({})); alert(d.error || 'Chyba při dokončení'); }
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
            {!checklist ? (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '80px 0', color: mutedColor }}>
                    <Loader2 size={24} className="animate-spin" />
                </div>
            ) : (
                <Obsah checklist={checklist} onSave={ulozOdpoved} onDokoncit={dokoncit} onClose={onZpet}
                    isDark={isDark} iStyle={iStyle} mutedColor={mutedColor} isMobile={isMobile} rezervaDole={rezervaDole} />
            )}
        </div>
    );
}

function Obsah({ checklist, onSave, onDokoncit, onClose, isDark, iStyle, mutedColor, isMobile, rezervaDole }) {
    const polozky = checklist.polozky || checklist.polozky_snapshot || checklist.sablona?.polozky || [];
    const isDone = checklist.stav === 'dokonceno';
    const debounceRefs = useRef({});

    const debouncedSave = (polozkaId, data) => {
        clearTimeout(debounceRefs.current[polozkaId]);
        debounceRefs.current[polozkaId] = setTimeout(() => onSave(checklist.id, polozkaId, data), 500);
    };

    const vyplnenoCount = polozky.filter(p => p.odpoved).length;
    const povinneVyplneno = polozky.filter(p => p.povinne).every(p => p.odpoved);
    const pct = polozky.length > 0 ? Math.round((vyplnenoCount / polozky.length) * 100) : 0;

    // Status ikona per položka
    const statusIcon = (p) => {
        const o = p.odpoved;
        if (!o) return <div style={{ width: 20, height: 20, borderRadius: '50%', border: `2px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`, flexShrink: 0 }} />;
        if (o.mimo_limit || (p.typ_odpovedi === 'kontrola' && o.hodnota_vyber === 'NOK')) return <AlertTriangle size={16} style={{ color: '#EF4444', flexShrink: 0 }} />;
        return <CheckCircle2 size={16} style={{ color: '#10B981', flexShrink: 0 }} />;
    };

    // Typ badge
    const typLabel = { checkbox: '📋', kontrola: '✅', cislo: '📊', vyber: '🔘', foto: '📷', text: '📝' };

    return (
        <>
            {/* Header — kompaktní s progress kruhem */}
            <div style={{ padding: isMobile ? '6px 14px 12px' : '16px 24px', flexShrink: 0, borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}` }}>
                {isMobile && <ZpetTlacitko onClick={onClose} popis="Zpět na checklisty" sklo isDark={isDark} style={{ marginBottom: 8 }} />}
                <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                    {/* Progress kruh */}
                    <div style={{ position: 'relative', width: 48, height: 48, flexShrink: 0 }}>
                        <svg width="48" height="48" viewBox="0 0 48 48">
                            <circle cx="24" cy="24" r="20" fill="none" stroke={isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)'} strokeWidth="4" />
                            <circle cx="24" cy="24" r="20" fill="none" stroke={SECTION_COLOR} strokeWidth="4"
                                strokeDasharray={`${pct * 1.257} 125.7`} strokeLinecap="round"
                                transform="rotate(-90 24 24)" style={{ transition: 'stroke-dasharray 0.4s' }} />
                        </svg>
                        <span style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 700, color: SECTION_COLOR }}>{pct}%</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <h2 style={{ fontSize: 15, fontWeight: 700, color: 'var(--text-primary)', margin: 0, lineHeight: 1.2 }}>{checklist.nazev}</h2>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
                            <Badge color={STAV_BARVY[checklist.stav]}>{STAV_LABELS[checklist.stav]}</Badge>
                            <span style={{ fontSize: 11, color: mutedColor }}>{vyplnenoCount} z {polozky.length}</span>
                            {checklist.role && <span style={{ fontSize: 10, color: checklist.role.barva }}>{checklist.role.ikona} {checklist.role.nazev}</span>}
                        </div>
                        <div style={{ fontSize: 11.5, color: mutedColor, marginTop: 3 }}>
                            {[checklist.smena?.sablon?.kod, checklist.faze_id && ({ zahajeni: 'Zahájení', prubeh: 'Průběh', ukonceni: 'Ukončení' }[checklist.faze_id] || checklist.faze_id),
                                checklist.vyplnil && `${checklist.vyplnil.jmeno} ${checklist.vyplnil.prijmeni}`,
                                checklist.dokonceno_at && `dokončeno ${new Date(checklist.dokonceno_at).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' })}`].filter(Boolean).join(' · ')}
                        </div>
                    </div>

                </div>
            </div>

            {/* Body — položky */}
            <div style={{ flex: 1, overflow: 'auto', padding: isMobile ? '8px 0' : '8px 0' }}>
                {polozky.map((p, i) => {
                    const odpoved = p.odpoved;
                    const mimoLimit = odpoved?.mimo_limit;
                    const hasAlert = mimoLimit || (p.typ_odpovedi === 'kontrola' && odpoved?.hodnota_vyber === 'NOK');
                    return (
                        <div key={p.id} style={{
                            padding: isMobile ? '8px 16px' : '8px 24px',
                            background: hasAlert ? (isDark ? 'rgba(239,68,68,0.04)' : 'rgba(239,68,68,0.02)') : (i % 2 === 0 ? 'transparent' : isDark ? 'rgba(255,255,255,0.015)' : 'rgba(0,0,0,0.015)'),
                            borderLeft: hasAlert ? '3px solid #EF4444' : odpoved ? `3px solid #10B981` : '3px solid transparent',
                            transition: 'background 0.15s',
                        }}>
                            {/* Hlavní řádek */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 32 }}>
                                {statusIcon(p)}
                                <span style={{ fontSize: 13, color: 'var(--text-primary)', flex: 1, minWidth: 0, lineHeight: 1.3 }}>
                                    {p.text}
                                    {p.povinne && <span style={{ color: '#EF4444', fontSize: 11 }}> *</span>}
                                </span>

                                {/* Inline checkbox */}
                                {p.typ_odpovedi === 'checkbox' && (
                                    <input type="checkbox" checked={odpoved?.hodnota_bool || false}
                                        disabled={isDone}
                                        onChange={e => onSave(checklist.id, p.id, { hodnota_bool: e.target.checked })}
                                        className="accent-cyan-500" style={{ width: 20, height: 20, flexShrink: 0, cursor: isDone ? 'default' : 'pointer' }} />
                                )}

                                {/* Inline kontrola */}
                                {p.typ_odpovedi === 'kontrola' && (
                                    <div style={{ display: 'flex', gap: 3, flexShrink: 0 }}>
                                        {['OK', 'NOK', 'N/A'].map(m => {
                                            const sel = odpoved?.hodnota_vyber === m;
                                            return (
                                                <button key={m} disabled={isDone}
                                                    onClick={() => onSave(checklist.id, p.id, { hodnota_vyber: m })}
                                                    style={{
                                                        fontSize: 10, padding: '2px 8px', borderRadius: 3, fontWeight: 600, lineHeight: 1.4,
                                                        background: sel ? (m === 'OK' ? 'rgba(16,185,129,0.15)' : m === 'NOK' ? 'rgba(239,68,68,0.15)' : 'rgba(107,114,128,0.15)') : 'transparent',
                                                        border: `1px solid ${sel ? (m === 'OK' ? '#10B981' : m === 'NOK' ? '#EF4444' : '#6B7280') : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)'}`,
                                                        color: sel ? (m === 'OK' ? '#10B981' : m === 'NOK' ? '#EF4444' : '#6B7280') : mutedColor,
                                                        cursor: isDone ? 'default' : 'pointer',
                                                    }}>
                                                    {m}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>

                            {/* Rozšířený obsah — pod řádkem */}
                            {/* Kontrola NOK alert */}
                            {p.typ_odpovedi === 'kontrola' && odpoved?.hodnota_vyber === 'NOK' && (
                                <div style={{ marginLeft: 28, marginTop: 4 }}>
                                    <NokAlert title="Kontrola nesplněna" instrukce={p.instrukce_mereni} poznamkaAkce={odpoved?.poznamka_akce}
                                        isDone={isDone} onSave={val => debouncedSave(p.id, { poznamka_akce: val })} isDark={isDark} iStyle={iStyle} />
                                </div>
                            )}

                            {/* Parametr (cislo) */}
                            {p.typ_odpovedi === 'cislo' && (() => {
                                const param = p.parametr;
                                const limit = param?.aktualni_limit;
                                const minVal = limit?.min_hodnota ?? p.min_hodnota;
                                const maxVal = limit?.max_hodnota ?? p.max_hodnota;
                                const jednotka = p.jednotka || param?.jednotka || '';
                                const instrMereni = param?.instrukce_mereni || p.instrukce_mereni;
                                const hasValue = odpoved?.hodnota_cislo != null;
                                const isLow = hasValue && minVal != null && parseFloat(odpoved.hodnota_cislo) < parseFloat(minVal);
                                const isHigh = hasValue && maxVal != null && parseFloat(odpoved.hodnota_cislo) > parseFloat(maxVal);
                                const isOk = hasValue && !isLow && !isHigh;
                                return (
                                    <div style={{ marginLeft: 28, marginTop: 4 }}>
                                        {instrMereni && (
                                            <p style={{ fontSize: 10, color: isDark ? 'rgba(12,192,223,0.6)' : '#0891B2', margin: '0 0 4px', fontStyle: 'italic' }}>📋 {instrMereni}</p>
                                        )}
                                        {/* Stejné pole jako v Cockpitu — okamžitá kontrola při psaní */}
                                        <MereniPole item={p} odp={odpoved || {}} checklistId={checklist.id} disabled={isDone}
                                            isDark={isDark} mutedColor={mutedColor} mimoLimit={mimoLimit}
                                            onSave={(data) => onSave(checklist.id, p.id, data)} />
                                        {(isLow || isHigh) && (
                                            <NokAlert
                                                title={isLow ? `Pod minimem (${minVal}${jednotka ? ' '+jednotka : ''})` : `Nad maximem (${maxVal}${jednotka ? ' '+jednotka : ''})`}
                                                instrukce={isLow ? limit?.instrukce_min : limit?.instrukce_max}
                                                poznamkaAkce={odpoved?.poznamka_akce} isDone={isDone}
                                                onSave={val => debouncedSave(p.id, { poznamka_akce: val })} isDark={isDark} iStyle={iStyle} />
                                        )}
                                    </div>
                                );
                            })()}

                            {/* Výběr */}
                            {p.typ_odpovedi === 'vyber' && (() => {
                                let moznosti = [];
                                try { moznosti = JSON.parse(p.moznosti_json || '[]'); } catch {}
                                if (!Array.isArray(moznosti)) moznosti = [];
                                moznosti = moznosti.map(m => typeof m === 'string' ? { text: m } : m);
                                if (moznosti.length === 0) moznosti = [{ text: 'OK' }, { text: 'NOK', negativni: true }, { text: 'N/A' }];
                                const val = odpoved?.hodnota_vyber;
                                const isNeg = moznosti.find(m => m.text === val)?.negativni;
                                return (
                                    <div style={{ marginLeft: 28, marginTop: 4 }}>
                                        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                                            {moznosti.map(m => {
                                                const sel = val === m.text;
                                                return (
                                                    <button key={m.text} disabled={isDone}
                                                        onClick={() => onSave(checklist.id, p.id, { hodnota_vyber: m.text })}
                                                        style={{
                                                            fontSize: 11, padding: '3px 10px', borderRadius: 5, fontWeight: sel ? 600 : 400,
                                                            background: sel ? (m.negativni ? 'rgba(239,68,68,0.12)' : `${SECTION_COLOR}15`) : 'transparent',
                                                            border: `1px solid ${sel ? (m.negativni ? '#EF4444' : SECTION_COLOR) : isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'}`,
                                                            color: sel ? (m.negativni ? '#EF4444' : SECTION_COLOR) : mutedColor,
                                                            cursor: isDone ? 'default' : 'pointer',
                                                        }}>
                                                        {m.text}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {isNeg && <NokAlert title={`Negativní: ${val}`} instrukce={p.instrukce_mereni} poznamkaAkce={odpoved?.poznamka_akce}
                                            isDone={isDone} onSave={val => debouncedSave(p.id, { poznamka_akce: val })} isDark={isDark} iStyle={iStyle} />}
                                    </div>
                                );
                            })()}

                            {/* Foto */}
                            {p.typ_odpovedi === 'foto' && (
                                <div style={{ marginLeft: 28, marginTop: 4 }}>
                                    <FotoUpload checklistId={checklist.id} polozkaId={p.id} fotoCesta={odpoved?.foto_cesta}
                                        isDone={isDone} isDark={isDark} mutedColor={mutedColor}
                                        onUploaded={async () => { const res = await fetch(`/api/checklisty/${checklist.id}`); if (res.ok) onSave(checklist.id, p.id, { hodnota_bool: true }); }} />
                                </div>
                            )}

                            {/* Text */}
                            {p.typ_odpovedi === 'text' && (
                                <div style={{ marginLeft: 28, marginTop: 4 }}>
                                    <textarea defaultValue={odpoved?.hodnota_text || ''} disabled={isDone}
                                        onChange={e => debouncedSave(p.id, { hodnota_text: e.target.value })}
                                        style={{ ...iStyle, resize: 'none', minHeight: 36, fontSize: 12, padding: '5px 8px' }} placeholder="..." />
                                </div>
                            )}

                            {/* Poznámka toggle */}
                            <PolozkaNote odpoved={odpoved} isDone={isDone} mimoLimit={mimoLimit}
                                isNokKontrola={(p.typ_odpovedi === 'kontrola' && odpoved?.hodnota_vyber === 'NOK') || (p.typ_odpovedi === 'vyber' && (() => { try { const mm = JSON.parse(p.moznosti_json || '[]').map(m => typeof m === 'string' ? { text: m } : m); return mm.find(m => m.text === odpoved?.hodnota_vyber)?.negativni; } catch { return false; } })())}
                                onSave={val => debouncedSave(p.id, { poznamka_akce: val })} isDark={isDark} iStyle={iStyle} mutedColor={mutedColor} />
                        </div>
                    );
                })}
                {rezervaDole && isDone && <div aria-hidden="true" style={{ height: rezervaDole }} />}
            </div>

            {/* Footer */}
            {!isDone && (
                <div style={{ padding: isMobile ? `10px 16px calc(10px + ${rezervaDole || '0px'})` : '12px 24px', borderTop: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-default)'}`, flexShrink: 0, display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 11, color: mutedColor }}>{vyplnenoCount}/{polozky.length} hotovo</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button onClick={() => onDokoncit(checklist.id)}
                            disabled={!povinneVyplneno}
                            title={!povinneVyplneno ? 'Vyplňte povinné položky' : ''}
                            style={{
                            padding: '8px 20px', borderRadius: 7, fontSize: 13, fontWeight: 600,
                            background: povinneVyplneno ? '#10B981' : '#6B7280',
                            color: '#fff', border: 'none', cursor: povinneVyplneno ? 'pointer' : 'not-allowed',
                            opacity: povinneVyplneno ? 1 : 0.5,
                            display: 'flex', alignItems: 'center', gap: 4,
                        }}>
                        <CheckCircle2 size={14} />
                        Dokončit a odeslat
                    </button>
                    </div>
                </div>
            )}
        </>
    );
}

// ── NOK alert block — reusable ──
function NokAlert({ title, instrukce, poznamkaAkce, isDone, onSave, isDark, iStyle }) {
    return (
        <div style={{
            marginTop: 8, padding: '10px 12px', borderRadius: 7,
            background: isDark ? 'rgba(239,68,68,0.08)' : 'rgba(239,68,68,0.04)',
            border: '1px solid rgba(239,68,68,0.20)',
        }}>
            <p style={{ fontSize: 12, fontWeight: 600, marginBottom: 4, color: '#EF4444', margin: '0 0 4px' }}>{title}</p>
            {instrukce && (
                <p style={{ fontSize: 11, marginBottom: 6, color: isDark ? 'rgba(255,255,255,0.7)' : '#374151', margin: '0 0 6px' }}>{instrukce}</p>
            )}
            <div style={{ marginTop: 4 }}>
                <label style={{ fontSize: 10, fontWeight: 500, marginBottom: 4, display: 'block', color: '#EF4444' }}>Co se stalo / Provedená akce:</label>
                <textarea
                    defaultValue={poznamkaAkce || ''}
                    disabled={isDone}
                    onChange={e => onSave(e.target.value)}
                    style={{ ...iStyle, resize: 'none', minHeight: 44, borderColor: 'rgba(239,68,68,0.25)', fontSize: 12, background: isDark ? 'rgba(239,68,68,0.05)' : 'rgba(239,68,68,0.02)' }}
                    placeholder="Popište situaci a jakou akci jste provedli..." />
            </div>
        </div>
    );
}

// ── Foto upload ──
function FotoUpload({ checklistId, polozkaId, fotoCesta, isDone, isDark, mutedColor, onUploaded }) {
    const fileRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [url, setUrl] = useState(fotoCesta ? `/storage/${fotoCesta}` : null);

    const handleUpload = async (file) => {
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append('foto', file);
        fd.append('polozka_id', polozkaId);
        const res = await fetch(`/api/checklisty/${checklistId}/foto`, {
            method: 'POST',
            headers: { 'X-CSRF-TOKEN': csrf() },
            body: fd,
        });
        if (res.ok) {
            const data = await res.json();
            setUrl(data.foto_url);
            onUploaded?.();
        }
        setUploading(false);
        if (fileRef.current) fileRef.current.value = '';
    };

    return (
        <div>
            {url ? (
                <div style={{ position: 'relative', display: 'inline-block' }}>
                    <img src={url} alt="Foto"
                        style={{ maxWidth: 240, maxHeight: 180, borderRadius: 7, objectFit: 'cover',
                            border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)'}` }} />
                    {!isDone && (
                        <button onClick={() => fileRef.current?.click()}
                            style={{ position: 'absolute', bottom: 6, right: 6, fontSize: 10, padding: '3px 8px', borderRadius: 5, background: 'rgba(0,0,0,0.6)', color: '#fff', border: 'none', cursor: 'pointer' }}>
                            Změnit
                        </button>
                    )}
                </div>
            ) : (
                <button onClick={() => fileRef.current?.click()} disabled={isDone || uploading}
                    style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                        width: '100%', padding: '16px', borderRadius: 7,
                        border: `2px dashed ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'}`,
                        background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)',
                        color: mutedColor, fontSize: 12, cursor: isDone ? 'default' : 'pointer',
                    }}>
                    {uploading ? 'Nahrávám...' : 'Vyfotit nebo nahrát'}
                </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" capture="environment" style={{ display: 'none' }}
                onChange={e => handleUpload(e.target.files?.[0])} />
        </div>
    );
}

// ── Poznámka k položce — toggle ──
function PolozkaNote({ odpoved, isDone, mimoLimit, isNokKontrola, onSave, isDark, iStyle, mutedColor }) {
    const hasNote = !!odpoved?.poznamka_akce;
    const [open, setOpen] = useState(hasNote);

    if (mimoLimit || isNokKontrola) return null;

    return (
        <div style={{ marginTop: 6 }}>
            {!open && (
                <button type="button" onClick={() => setOpen(true)}
                    style={{ fontSize: 11, color: isDark ? 'rgba(255,255,255,0.35)' : 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
                    Poznámka
                </button>
            )}
            {open && (
                <div>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                        <label style={{ fontSize: 10, fontWeight: 500, color: mutedColor }}>Poznámka</label>
                        {!hasNote && !isDone && (
                            <button type="button" onClick={() => setOpen(false)}
                                style={{ fontSize: 10, color: mutedColor, background: 'none', border: 'none', cursor: 'pointer' }}>Skrýt</button>
                        )}
                    </div>
                    <textarea
                        defaultValue={odpoved?.poznamka_akce || ''}
                        disabled={isDone}
                        onChange={e => onSave(e.target.value)}
                        style={{ ...iStyle, resize: 'none', minHeight: 36, fontSize: 12, padding: '6px 10px' }}
                        placeholder="Volitelná poznámka..." />
                </div>
            )}
        </div>
    );
}

